<template>
  <b-card v-if="!!option" id="page-catalog-form" no-body class="mb-0">
    <b-card-header>
      <b-card-title
        ><h2>
          {{ $t(getTitle()) }} {{ $t("pages.catalogs.title").toLowerCase() }}
        </h2></b-card-title
      >
      <b-button variant="primary" @click="goBack">
        <feather-icon icon="ArrowLeftIcon" />
        {{ $t("back") }}
      </b-button>
    </b-card-header>

    <b-card-body>
      <validation-observer ref="simpleRules">
        <b-form>
          <b-row>
            <b-col cols="12" md="6">
              <b-form-group :label="$t(label('name'))" label-for="name">
                <validation-provider
                  #default="{ errors }"
                  :name="$t(label('name'))"
                  rules="required"
                >
                  <b-form-input
                    id="name"
                    v-model="catalog.name"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="$t(label('name'))"
                    :disabled="!isSaveMode"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col v-if="option != 2" cols="12" sm="6">
              <b-form-group :label="$t(label('item'))" label-for="item">
                <b-form-input
                  id="item"
                  v-model="item"
                  :disabled="option === 2 ? true : false"
                  :placeholder="$t(label('item'))"
                />
              </b-form-group>
            </b-col>

            <b-col v-if="option != 2" cols="12" md="6">
              <b-form-group :label="$t(label('value'))" label-for="value">
                <b-form-input
                  id="value"
                  v-model="value"
                  :disabled="option === 2 ? true : false"
                  :placeholder="$t(label('value'))"
                />
              </b-form-group>
            </b-col>

            <b-col
              v-if="option != 2"
              class="d-flex align-items-center justify-content-end"
              cols="12"
            >
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                @click="add"
              >
                <feather-icon icon="PlusIcon" class="mr-25" />
                <span>{{ !editTable ? $t("add") : $t("edit") }}</span>
              </b-button>
            </b-col>

            <b-col class="mt-1" cols="12">
              <b-table
                id="catalog-table"
                responsive
                :head-variant="headVariant"
                :striped="striped"
                :bordered="bordered"
                :borderless="borderless"
                :outlined="outlined"
                :per-page="perPage"
                :current-page="currentPage"
                :busy="loading"
                :items="catalog.items"
                :fields="fields"
                class="d-none d-md-table shadow-sm !rounded"
              >
                <!-- A custom formatted header cell for field 'name' -->
                <template #head()="data">
                  <span class="text-black">{{
                    $t(labelHeader(data.label.toLowerCase()))
                  }}</span>
                </template>

                <template #cell(name)="data">
                  <slot name="cell-name" :data="data">
                    {{ data.item.name || "N/A" }}
                  </slot>
                </template>

                <template #cell(color)="data">
                  <slot name="cell-color" :data="data">
                    {{ data.item.color || "N/A" }}
                  </slot>
                </template>

                <template #cell(actions)="data" v-if="option != 2">
                  <feather-icon
                    v-if="option == 3"
                    class="text-primary mr-25 cursor-pointer"
                    aria-label="Edit details"
                    size="18"
                    icon="EditIcon"
                    @click="onEdit(data.item)"
                  />
                  <feather-icon
                    class="text-danger cursor-pointer"
                    aria-label="Delete details"
                    size="18"
                    icon="Trash2Icon"
                    @click="onDelete(data.item)"
                  />
                </template>
              </b-table>

              <!-- pagination -->
              <div class="m-0" v-if="catalog.items.length">
                <b-col
                  cols="12"
                  class="d-flex align-items-center justify-content-end"
                >
                  <b-pagination
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    @change="handlePageChange"
                    pills
                    first-number
                    last-number
                  >
                    <template #prev-text>
                      <feather-icon size="18" icon="ChevronLeftIcon" />
                    </template>
                    <template #next-text>
                      <feather-icon
                        size="18"
                        icon="ChevronRightIcon"
                      /> </template
                  ></b-pagination>
                </b-col>
              </div>
            </b-col>

            <b-col
              class="mt-3 d-flex justify-content-end align-items-center"
              cols="12"
            >
              <b-button
                v-if="option != 2"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="submit"
                variant="primary"
                class="mr-1"
                @click.prevent="onSubmit"
              >
                {{ $t("save") }}
              </b-button>
              <b-button
                v-if="option == 1"
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="reset"
                variant="outline-secondary"
                @click="onCancel"
              >
                {{ $t("cancel") }}
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-card-body>
  </b-card>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import { ValidationProvider, ValidationObserver, localize } from "vee-validate";
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BDropdown,
  BDropdownItem,
  BPagination,
  BFormGroup,
  BFormCheckbox,
  BForm,
  BFormTextarea,
  BAlert,
  BFormSelect,
  BModal,
  BToast,
  BTable,
  BListGroup,
  BListGroupItem,
  BBadge,
} from "bootstrap-vue";
import router from "@/router";
import { required, email, confirmed, password } from "@validations";
import { FormWizard, TabContent } from "vue-form-wizard";
import "vue-form-wizard/dist/vue-form-wizard.min.css";
import Ripple from "vue-ripple-directive";

import vSelect from "vue-select";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { getParams, showLoading, stopLoading } from "@core/utils/utils";

import {
  getCatalog,
  createOrEditCatalog,
  addItemCatalog,
  editItemCatalog,
  deleteItemCatalog,
} from "@core/api/request/catalogs";

const catalogTemplate = {
  guid: null,
  name: null,
  items: [],
};

export default {
  components: {
    FormWizard,
    TabContent,
    BAlert,
    BModal,
    ValidationProvider,
    ValidationObserver,
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BForm,
    vSelect,
    BFormTextarea,
    BFormSelect,
    BToast,
    BListGroup,
    BListGroupItem,
    BBadge,
  },
  directives: {
    Ripple,
  },
  data: () => ({
    loading: false,
    totalRows: 0,
    currentPage: 1,
    perPage: 10,
    pageOptions: [10, 50, 100],
    hasNextPage: true,
    items: [],
    headVariant: "light",
    striped: false,
    outlined: false,
    bordered: true,
    borderless: true,

    isSaveMode: true,
    required,
    dir: "ltr",
    catalog: Object.assign(catalogTemplate),
    item: null,

    option: null,
    idItem: null,
    editTable: false,
    value: null,

    fields: [
      { label: "name", key: "name", sortable: false },
      { label: "value", key: "color", sortable: false },
      { label: "action", key: "actions", sortable: false },
    ],
  }),

  computed: {},

  created() {
    localize("es");
  },

  mounted() {
    this.initialize();
  },
  methods: {
    label(field) {
      return `pages.catalogs.form.label.${field}`;
    },

    labelHeader(field) {
      return `pages.catalogs.table.tableHeader.${field}`;
    },

    getTitle() {
      if (this.option === 1) return "register";
      else if (this.option === 2) return "show";
      else if (this.option === 3) return "edit";
    },

    initialize() {
      this.option = this.$route.params.option;

      if (this.option === undefined) {
        router.push({ name: "catalogs" }).catch(() => {});
      }

      if (this.option === 3 || this.option === 2) {
        this.onCancel();
        if (this.$route.params.guid === undefined) {
          router.push({ name: "catalogs" }).catch(() => {});
        }
        this.getCatalog(this.$route.params.guid);
      } else {
        this.onCancel();
      }
    },

    add() {
      if (!this.item) {
        this.alertToast(
          "danger",
          this.$t("Alert"),
          this.$t("pages.catalogs.itemNameRequired"),
        );
        return;
      }

      if (!this.catalog.guid) {
        const random = () => ((1 + Math.random()) * 0x100000) | 0;
        const block = () =>
          (+moment.now() + random() + random() + random()).toString(16);
        const id = `${block()}-${block()}-${block()}`;
        this.catalog.items.push({
          id: null,
          name: this.item,
          color: this.value,
        });
        this.totalRows = this.catalog.items.length;
      } else {
        this.editRow({ item: this.item, color: this.value });
      }
      this.item = null;
      this.value = null;
    },

    onDelete(item) {
      this.$swal({
        title: this.$t("areYouSure"),
        text: this.$t("willNotBeAble"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("yesDelete"),
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          if (item.id) {
            this.deleteItem(item);
          } else {
            const index = this.catalog.items.findIndex(
              (model) => model.guid === item.guid,
            );
            this.catalog.items.splice(index, 1);
          }
          this.totalRows = this.catalog.items.length;
        } else if (result.dismiss === "cancel") {
          this.message(
            this.$t("canceled"),
            this.$t("registerDeletedCanceled"),
            "error",
          );
        }
      });
    },

    editRow(item) {
      const data = {
        guidCatalog: this.catalog.guid,
        name: item.item,
        color: item.color,
        id: this.idItem,
      };

      showLoading();
      if (this.idItem) {
        editItemCatalog(data)
          .then((resp) => {
            stopLoading();
            this.idItem = null;
            this.editTable = false;
            this.getCatalog(this.catalog.guid);
          })
          .catch((error) => {
            stopLoading();
          });
      } else {
        addItemCatalog(data)
          .then((resp) => {
            stopLoading();
            this.getCatalog(this.catalog.guid);
          })
          .catch((error) => {
            stopLoading();
          });
      }
    },

    onEdit(item) {
      this.item = item.name;
      if (item.color) {
        this.value = item.color;
      }
      this.idItem = item.id;
      this.editTable = true;
    },

    handlePageChange(value) {
      this.currentPage = value;
      this.getCatalog(this.catalog.guid);
    },

    chargeParams(guidCatalog) {
      const arrParams = [];

      arrParams.push(`itemsPage,${this.currentPage}`);

      arrParams.push(`itemsRows,${this.perPage}`);

      arrParams.push(`guidCatalog,${guidCatalog}`);

      return getParams(arrParams);
    },

    getCatalog(id) {
      showLoading();
      const param = this.chargeParams(id);
      getCatalog(param)
        .then((resp) => {
          stopLoading();
          this.catalog = Object.assign(this.catalog, {
            guid: resp.data.guid || null,
            name: resp.data.name || null,
            items: [
              ...this.catalog.items,
              ...(resp?.data?.listOfItems?.items || []),
            ],
          });
          this.totalRows = resp?.data?.listOfItems?.totalRows || 0;
          this.hasNextPage =
            resp?.data?.listOfItems?.currentPage <
              resp?.data?.listOfItems?.totalPages || 0;
        })
        .catch((error) => {
          stopLoading();
          this.alertToast("danger", error.message);
        });
    },

    onSubmit() {
      if (!this.catalog?.items.length) {
        this.alertToast(
          "danger",
          "Alerta",
          "No existen item asociados a al catalogo",
        );
        return;
      }

      this.$refs.simpleRules.validate().then((success) => {
        if (success) {
          showLoading();

          const guid = this.option === 1 ? null : this.catalog.guid;

          createOrEditCatalog(this.catalog, guid)
            .then(() => {
              this.alertToast("success", "Registro exitoso");
              this.goBack();
              stopLoading();
            })
            .catch((error) => {
              stopLoading();
              this.alertToast("danger", error.message);
            });
        }
      });
    },

    goBack() {
      this.$refs.simpleRules.reset();
      router.back();
      this.onCancel();
    },

    onCancel() {
      this.catalog = Object.assign({
        guid: null,
        name: null,
        items: [],
      });
    },

    message(title, text, icon, html) {
      this.$swal({
        title,
        text,
        html,
        icon,
        customClass: {
          confirmButton: "btn btn-success",
        },
      });
    },

    alertToast(type = null, title = null, message = null, icon) {
      this.$bvToast.toast(message, {
        title,
        variant: type,
        solid: true,
        icon,
      });
    },

    validationCatalogs() {
      return new Promise((resolve, reject) => {
        this.$refs.catalogsRules.validate().then((success) => {
          if (success) {
            resolve(true);
          } else {
            reject();
          }
        });
      });
    },

    validationCatalogDetails() {
      return new Promise((resolve, reject) => {
        this.$refs.catalogDetailRules.validate().then((success) => {
          if (success) {
            resolve(true);
          } else {
            reject();
          }
        });
      });
    },

    deleteItem(item) {
      const arrParams = [
        `guidCatalog,${this.catalog.guid}`,
        `itemId,${item.id}`,
      ];
      const params = getParams(arrParams);
      showLoading();
      deleteItemCatalog(params)
        .then((resp) => {
          stopLoading();
          this.getCatalog(this.catalog.guid);
        })
        .catch((error) => {
          stopLoading();
        });
    },
  },
};
</script>

<style lang="scss">
.title {
  margin-bottom: 3vh;
}

@import "@core/scss/vue/libs/vue-select.scss";
</style>
